import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import logo from '../../assets/images/logo.svg';
import notificationReadIcon from '../../assets/icons/notifications-read.svg';
import dashboardIcon from '../../assets/icons/dashboard.svg';
import transactionsIcon from '../../assets/icons/transactions.svg';
import requestsIcon from '../../assets/icons/requests.svg';
import rapprochementsIcon from '../../assets/icons/rapprochements.svg';
import LoggedUserCard from '../LoggedUserCard';
import NotificationPanel from '../NotificationPanel';
import notificationAPI from '../../services/notificationAPI';

const Header = () => {
  const history = useHistory();
  const location = useLocation();
  const { user } = useAuth();
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch unread count
  const fetchUnreadCount = async () => {
    try {
      const response = await notificationAPI.getUnreadCount();
      setUnreadCount(response.unreadCount || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  // Toggle notification panel
  const handleNotificationClick = (event) => {
    event.stopPropagation();
    setIsNotificationPanelOpen(!isNotificationPanelOpen);
  };


  // Update unread count when panel closes
  const handleNotificationPanelClose = () => {
    setIsNotificationPanelOpen(false);
    fetchUnreadCount(); // Refresh count
  };


  useEffect(() => {
    fetchUnreadCount();
    // Refresh count every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Close notification panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isNotificationPanelOpen && 
          !event.target.closest('.notification-panel') && 
          !event.target.closest('.notification-trigger')) {
        setIsNotificationPanelOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isNotificationPanelOpen]);

  const navigationItems = [
    { 
      label: 'Tableau de Bord', 
      path: '/dashboard', 
      icon: dashboardIcon,
      roles: ['MANAGER', 'AGENT']
    },
    { 
      label: 'Transactions', 
      path: '/transactions', 
      icon: transactionsIcon,
      roles: ['MANAGER', 'AGENT', 'POINTAGE_CLIENT', 'POINTAGE_COMPTABILITE']
    },
    {
      label: 'Demandes',
      path: '/requests',
      icon: requestsIcon,
      roles: ['MANAGER', 'AGENT']
    },
    {
      label: 'Rapprochements',
      path: '/rapprochements',
      icon: rapprochementsIcon,
      roles: ['MANAGER']
    },
  ];

  const handleNavigation = (path, event) => {
    // Check if we're already on this route
    // Allow middle-click, Ctrl+click, Cmd+click to open in new tab (default browser behavior)
    if (event.button === 1 || event.ctrlKey || event.metaKey) {
      // Let the browser handle it naturally
      return;
    }
    
    event.preventDefault();
    
    const isExactPath = location.pathname === path;

    if (isExactPath) {
      // Already on the exact page: clear params or reload
      if (location.search) {
        window.location.href = path;
      } else {
        window.location.reload();
      }
    } else {
      // Sub-page or different section: always navigate to base path
      history.push(path);
    }
  };


  const isActiveRoute = (path) => {
    // Special case: if we're at root (/) and checking dashboard, it should be active
    if (path === '/dashboard' && location.pathname === '/') {
      return true;
    }
    // For all other cases, use startsWith
    return location.pathname.startsWith(path);
  };

  const canAccessRoute = (item) => {
    if (!user || !item.roles.includes(user.role)) {
      return false;
    }
    
    // Hide "Demandes" for AGENTs assigned to a mobile caisse
    if (item.path === '/requests' && user.role === 'AGENT' && user.assignedCaisses && user.assignedCaisses.length > 0) {
      const hasMobileCaisse = user.assignedCaisses.some(caisse => caisse.type === 'MOBILE');
      if (hasMobileCaisse) {
        return false;
      }
    }
    
    return true;
  };

  return (
    <header className="relative w-full" style={{ position: 'sticky', top: '0px', zIndex: 1000, backgroundColor: '#FFFFFF' }}>
      {/* Low opacity rectangle background - fully responsive */}
      <div 
        className="absolute"
        style={{
          top: '-83px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '100vw',
          height: '193px',
          background: '#0985CF',
          opacity: 0.15,
          filter: 'blur(50px)',
          zIndex: 0
        }}
      />
      
      <div className="relative flex items-center justify-between px-4 sm:px-6 py-4" style={{ zIndex: 1 }}>
        {/* Logo - Always visible */}
        <div className="flex items-center flex-shrink-0">
          <img 
            src={logo} 
            alt="Logo" 
            className="w-[120px] sm:w-[180px] h-[36px] sm:h-[54px]"
          />
        </div>

        {/* Vertical separator line - Hidden on mobile */}
        <div 
          className="h-10 w-px mx-1 hidden sm:block"
          style={{
            backgroundColor: '#000000',
            opacity: 0.05
          }}
        />

        {/* Navigation - Scrollable on mobile */}
        <div className="relative flex-1 mx-2 sm:mx-4 min-w-0 flex justify-center">
          <nav className="flex items-center space-x-1 sm:space-x-2 md:space-x-4 overflow-x-auto scrollbar-hide">
            {/* Left fade indicator */}
            <div className="absolute left-0 top-0 bottom-0 w-4 bg-gradient-to-r from-white to-transparent pointer-events-none z-10 sm:hidden"></div>
            {/* Right fade indicator */}
            <div className="absolute right-0 top-0 bottom-0 w-4 bg-gradient-to-l from-white to-transparent pointer-events-none z-10 sm:hidden"></div>
          {navigationItems.map((item) => {
            if (!canAccessRoute(item)) return null;
            
            const isActive = isActiveRoute(item.path);
            const textColor = isActive ? '#007BC6' : '#9A9A9A';
            const iconColor = isActive ? '#007BC6' : '#9A9A9A';
            
            return (
              <a
                key={item.path}
                href={item.path}
                onClick={(e) => handleNavigation(item.path, e)}
                onMouseDown={(e) => {
                  // Allow middle-click to open in new tab
                  if (e.button === 1) {
                    e.preventDefault();
                    window.open(item.path, '_blank', 'noopener,noreferrer');
                  }
                }}
                onMouseUp={(e) => {
                  // Remove mouse focus state after clicking to avoid persistent darker styling
                  try { e.currentTarget.blur(); } catch (err) { /* ignore */ }
                }}
                className="flex items-center space-x-1 sm:space-x-1.5 transition-colors duration-200 hover:opacity-80 px-1 sm:px-2 py-1 rounded flex-shrink-0 min-w-fit text-xs sm:text-sm"
                style={{
                  fontFamily: 'Montserrat',
                  fontWeight: isActive ? 700 : 500,
                  outline: 'none',
                  lineHeight: '20px',
                  textTransform: 'none',
                  letterSpacing: '0px',
                  whiteSpace: 'nowrap',
                  textDecoration: 'none',
                  cursor: 'pointer'
                }}
              >
                <img 
                  src={item.icon} 
                  alt={`${item.label} icon`}
                  className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0"
                  style={{ filter: `brightness(0) saturate(100%) ${iconColor === '#007BC6' ? 'invert(27%) sepia(96%) saturate(1234%) hue-rotate(194deg) brightness(95%) contrast(101%)' : 'invert(60%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%)'}` }}
                />
                <span style={{ color: textColor }} className="truncate">
                  {item.label}
                </span>
              </a>
            );
          })}
          </nav>
        </div>

        {/* User Profile & Actions */}
        <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
          {/* Notifications */}
          <div className="relative notification-trigger">
            <div 
              className="absolute inset-0"
              style={{
                width: '32px',
                height: '32px',
                background: '#FFFFFF',
                borderRadius: '20px',
                opacity: 0.4
              }}
            />
            <button 
              onClick={handleNotificationClick}
              className="relative text-[#9A9A9A] hover:text-[#007BC6] transition-colors duration-200 flex items-center justify-center w-8 h-8"
              style={{ position: 'relative' }}
            >
              <img 
                src={notificationReadIcon} 
                alt="Notifications" 
                className="w-4 h-4" 
              />
              {unreadCount > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '-4px',
                    right: '-4px',
                    minWidth: '18px',
                    height: '18px',
                    backgroundColor: '#0AD69F',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: unreadCount > 9 ? '0 4px' : '0',
                    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Montserrat',
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#FFFFFF',
                      lineHeight: 1,
                      textAlign: 'center'
                    }}
                  >
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </Typography>
                </Box>
              )}
            </button>
            
            {/* Notification Panel - Now positioned relative to this container */}
            <NotificationPanel 
              isOpen={isNotificationPanelOpen} 
              onClose={handleNotificationPanelClose}
              onUnreadCountChange={setUnreadCount}
            />
          </div>



          {/* User Profile with Dropdown - Using Reusable Component */}
          <LoggedUserCard />
        </div>
      </div>


    </header>
  );
};

export default Header;
